import React, { useMemo, useRef, useState } from 'react';
import { Box, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import { useRpc } from '../../tracking/useRpc';
import { execTiles, formatDateRange, keyPoints, periodRange, singaporeToday, type DrillSpec, type OwnerAnalytics, type OwnerPricing, type PeriodKey } from './ownerData';
import type { OwnerTeam } from './teamData';
import { LoadError, PeriodSelect, StatTile } from './parts';
import { Panel, TileGrid } from '../DashboardKit';
import RecordsDialog from './RecordsDialog';
import { CostsSection, CoverageSection, DemandSection, PricingSection, RevenueSection, SpeedSection } from './sections';

const NAV = [
  { id: 'revenue', label: 'Revenue & customers' },
  { id: 'demand', label: 'Demand & conversion' },
  { id: 'speed', label: 'Speed' },
  { id: 'pricing', label: 'Pricing quality' },
  { id: 'costs', label: 'Costs' },
  { id: 'coverage', label: 'Data coverage' },
];

const OwnerAnalyticsTab: React.FC<{ period: PeriodKey; onPeriodChange: (p: PeriodKey) => void }> = ({ period, onPeriodChange }) => {
  const today = singaporeToday();
  const range = useMemo(() => periodRange(period, today), [period, today]);
  const rangeKey = `${range.from}|${range.to}`;
  const trailing = useMemo(() => periodRange('full4', today), [today]);
  const trailingKey = `${trailing.from}|${trailing.to}`;
  const sameAsTrailing = rangeKey === trailingKey;

  // index.html sets <base href="/">, so a bare href="#demand" resolves to "/#demand" and
  // leaves the page for the RFQ list. Scroll in place instead, clear of the sticky bar.
  const barRef = useRef<HTMLDivElement>(null);
  const jumpTo = (e: React.MouseEvent, id: string) => {
    const target = document.getElementById(id);
    if (!target || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    const offset = (barRef.current?.offsetHeight ?? 0) + 8;
    window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#${id}`);
  };

  const [drill, setDrill] = useState<DrillSpec | null>(null);
  const rangeArgs = { p_from: range.from, p_to: range.to };

  // get_owner_analytics: everything except pricing. On failure the last
  // loaded period stays on screen, labelled with its own dates.
  const main = useRpc<OwnerAnalytics>('get_owner_analytics', rangeArgs);
  // Pricing reads its own precomputed table, so it loads on its own: a slow or
  // failed call blanks only the pricing figures. The headline pricing tile
  // always uses the last 4 full quarters; the Pricing section uses the period.
  // When they are the same window it is one call.
  const trailingPricing = useRpc<OwnerPricing>('get_owner_pricing', { p_from: trailing.from, p_to: trailing.to }, { keepPrevious: false });
  const rangePricing = useRpc<OwnerPricing>('get_owner_pricing', rangeArgs, { keepPrevious: false, enabled: !sameAsTrailing });
  // Quoted value panel (Demand & conversion): system quotes per month.
  const team = useRpc<OwnerTeam>('get_owner_team', rangeArgs, { keepPrevious: false });

  const sectionPricing = sameAsTrailing ? trailingPricing : rangePricing;

  const data = main.data;
  const loading = main.loading;
  // A failure is shown once no newer call is in flight.
  const mainError = loading ? null : main.error;
  const tiles = useMemo(
    () => (data ? execTiles(data, trailingPricing.data, trailingPricing.error) : []),
    [data, trailingPricing.data, trailingPricing.error],
  );
  const points = useMemo(() => (data ? keyPoints(data) : []), [data]);

  const header = (
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '100ch', mb: 2 }}>
      How the business is doing: revenue and customers, RFQs and how fast they are answered, pricing quality and what the data covers.
      Every number opens the records it was counted from.
    </Typography>
  );

  if (!data) {
    return (
      <Box>
        {header}
        {mainError ? (
          <LoadError what="the business overview" message={mainError} onRetry={main.reload} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress aria-label="Loading the business overview" />
            <Typography variant="body2" color="text.secondary">
              Loading the business overview - this takes a few seconds.
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {header}

      {/* On a phone the plain-words summary comes first: seven tiles stacked are a long scroll before it. */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ order: { xs: 2, sm: 1 } }}>
          <Typography variant="overline" component="h2" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.6 }}>
            Executive summary · last 4 full quarters ({formatDateRange(data.summary.trailing.from, data.summary.trailing.to)}) compared with the 4 before
          </Typography>
          <TileGrid columns={{ xs: 1, sm: 2, lg: 4 }} sx={{ mb: 2 }}>
            {tiles.map((t) => (
              <StatTile key={t.id} tile={t} onDrill={setDrill} onRetry={trailingPricing.reload} />
            ))}
          </TileGrid>
        </Box>

        {points.length > 0 && (
          <Panel title="In short" sx={{ mb: { xs: 2, sm: 3 }, height: 'auto', order: { xs: 1, sm: 2 } }}>
            <Box component="ul" sx={{ m: 0, pl: 2.5, columnGap: 6, columns: { xs: 1, xl: 2 } }}>
              {points.map((p) => (
                <Typography component="li" variant="body2" key={p} sx={{ mb: 0.75, breakInside: 'avoid' }}>
                  {p}
                </Typography>
              ))}
            </Box>
          </Panel>
        )}
      </Box>

      <Paper
        ref={barRef}
        variant="outlined"
        component="nav"
        aria-label="Period and sections"
        sx={{ position: 'sticky', top: 0, zIndex: 2, px: { xs: 1.5, xl: 2 }, py: 1.5, mb: 3, display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 1, alignItems: 'center', bgcolor: 'background.paper' }}
      >
        <PeriodSelect value={period} onChange={onPeriodChange} label="Period for the sections below" />
        <Typography variant="body2" color="text.secondary" aria-live="polite">
          {loading ? 'Loading…' : `Showing ${formatDateRange(data.range.from, data.range.to)}`}
        </Typography>
        {loading && <CircularProgress size={18} aria-label="Loading the selected period" />}
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            ml: { md: 'auto' },
            // One swipeable row on a phone instead of three wrapped rows of sticky chips.
            flexWrap: { xs: 'nowrap', md: 'wrap' },
            overflowX: { xs: 'auto', md: 'visible' },
            width: { xs: '100%', md: 'auto' },
            pb: { xs: 0.25, md: 0 },
            scrollbarWidth: 'none',
          }}
        >
          {NAV.map((n) => (
            <Chip
              key={n.id}
              label={n.label}
              size="small"
              variant="outlined"
              component="a"
              href={`${window.location.pathname}${window.location.search}#${n.id}`}
              onClick={(e: React.MouseEvent) => jumpTo(e, n.id)}
              clickable
              sx={{ flexShrink: 0 }}
            />
          ))}
        </Box>
      </Paper>

      {mainError && (
        <Box sx={{ mb: 2 }}>
          <LoadError what="the selected period" message={mainError} onRetry={main.reload} severity="warning">
            Still showing {formatDateRange(data.range.from, data.range.to)}.
          </LoadError>
        </Box>
      )}

      <Box sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 150ms' }}>
        <RevenueSection data={data} onDrill={setDrill} />
        <DemandSection
          data={data}
          onDrill={setDrill}
          team={team.data}
          teamError={team.error}
          onRetryTeam={team.reload}
        />
        <SpeedSection data={data} onDrill={setDrill} />
        <PricingSection data={data} pricing={sectionPricing.data} error={sectionPricing.error} onRetry={sectionPricing.reload} onDrill={setDrill} />
        <CostsSection data={data} onDrill={setDrill} />
        <CoverageSection data={data} onDrill={setDrill} />
      </Box>

      <RecordsDialog spec={drill} onClose={() => setDrill(null)} />
    </Box>
  );
};

export default OwnerAnalyticsTab;
